import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  HStack,
  Spinner,
  Table,
  TableContainer,
  Tab,
  TabList,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useUser } from '../../contexts/UserContext';
import { listLeads, updateLeadStatus } from '../../api/leads';
import {
  bookingUrl,
  formatDate,
  LeadDetailsContent,
  LeadStatusControl,
  statusColor,
  statusLabel,
  assignedText,
  toolText,
} from './LeadDetails';

const STATUS_TABS = [
  { label: 'New', value: 'new' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Booked', value: 'booked' },
  { label: 'Converted', value: 'converted' },
  { label: 'Archived', value: 'archived' },
];

const LeadsList = () => {
  const { user } = useUser();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [activeStatus, setActiveStatus] = useState('new');
  const [isLoading, setIsLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [leads, setLeads] = useState([]);
  const [updatingLeadIds, setUpdatingLeadIds] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);

  const detailsDrawer = useDisclosure();

  const loadLeads = useCallback(async () => {
    setIsLoading(true);
    setIsUnauthorized(false);

    try {
      const payload = await listLeads(activeStatus ? { status: activeStatus } : {});
      setLeads(payload);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        window.location.href = '/login';
        return;
      }
      if (status === 403) {
        setIsUnauthorized(true);
        setLeads([]);
      } else {
        setLeads([]);
        toast({ title: 'Could not load leads', status: 'error', duration: 4000, isClosable: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeStatus, toast]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const sortedLeads = useMemo(() => {
    return [...leads].sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [leads]);

  const handleOpenDetails = (lead) => {
    setSelectedLead(lead);
    detailsDrawer.onOpen();
  };

  const persistStatusUpdate = async (leadId, nextStatus) => {
    const previousLeads = leads;
    const previousSelectedLead = selectedLead;
    const movedOutOfActiveTab = nextStatus !== activeStatus;

    setLeads((prev) => prev.map((lead) => (lead.id === leadId ? { ...lead, status: nextStatus } : lead)));
    setSelectedLead((prev) => (prev?.id === leadId ? { ...prev, status: nextStatus } : prev));
    setUpdatingLeadIds((prev) => ({ ...prev, [leadId]: true }));

    try {
      const updated = await updateLeadStatus(leadId, nextStatus);
      setLeads((prev) => prev.map((lead) => (lead.id === leadId ? { ...lead, ...updated } : lead)));
      setSelectedLead((prev) => (prev?.id === leadId ? { ...prev, ...updated } : prev));
      toast({ title: 'Lead status updated', status: 'success', duration: 2500, isClosable: true });
      await loadLeads();
      if (detailsDrawer.isOpen && selectedLead?.id === leadId && movedOutOfActiveTab) {
        detailsDrawer.onClose();
        setSelectedLead(null);
      }
    } catch (error) {
      setLeads(previousLeads);
      setSelectedLead(previousSelectedLead);
      const status = error?.response?.status;
      toast({
        title: status === 403 || status === 404 ? 'Not authorized to update this lead' : 'Could not update status',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setUpdatingLeadIds((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  const renderStatus = (lead) => (
    <LeadStatusControl
      lead={lead}
      user={user}
      isUpdating={Boolean(updatingLeadIds[lead.id])}
      onChange={(status) => persistStatusUpdate(lead.id, status)}
    />
  );

  const renderMobile = () => (
    <VStack align="stretch" spacing={3}>
      {sortedLeads.map((lead) => (
        <Card key={lead.id} bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Button size="sm" variant="link" onClick={() => handleOpenDetails(lead)}>
                  {lead.name || '—'}
                </Button>
                <Badge colorScheme={statusColor(lead.status)}>{statusLabel(lead.status)}</Badge>
              </HStack>
              <Text fontSize="sm">Organization: {lead.organization_name || '—'}</Text>
              <Text fontSize="sm">Tool: {toolText(lead)}</Text>
              <Text fontSize="sm">Created: {formatDate(lead.created_at)}</Text>
              <Text fontSize="sm">Assigned: {assignedText(lead)}</Text>
              <HStack justify="space-between" pt={1}>
                {renderStatus(lead)}
                <Button size="sm" variant="outline" onClick={() => handleOpenDetails(lead)}>
                  Details
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      ))}
    </VStack>
  );

  const renderDesktop = () => (
    <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
      <CardBody>
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>School</Th>
                <Th>Tool</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Assigned</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedLeads.map((lead) => (
                <Tr key={lead.id}>
                  <Td>
                    <Button size="sm" variant="link" onClick={() => handleOpenDetails(lead)}>
                      {lead.name || '—'}
                    </Button>
                  </Td>
                  <Td>{lead.organization_name || '—'}</Td>
                  <Td>{toolText(lead)}</Td>
                  <Td>{renderStatus(lead)}</Td>
                  <Td>{formatDate(lead.created_at)}</Td>
                  <Td>{assignedText(lead)}</Td>
                  <Td>
                    <Button size="sm" variant="outline" onClick={() => handleOpenDetails(lead)}>
                      Details
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Heading size="lg" mb={1}>Leads</Heading>
      <Text color="gray.500" mb={4}>Review incoming leads and move them through your pipeline.</Text>

      <Tabs
        index={STATUS_TABS.findIndex((status) => status.value === activeStatus)}
        onChange={(index) => setActiveStatus(STATUS_TABS[index]?.value || 'new')}
        variant="soft-rounded"
        colorScheme="blue"
        mb={4}
      >
        <TabList overflowX="auto" py={1}>
          {STATUS_TABS.map((status) => (
            <Tab key={status.value} whiteSpace="nowrap">{status.label}</Tab>
          ))}
        </TabList>
      </Tabs>

      {isLoading ? (
        <HStack justify="center" py={12}><Spinner size="lg" /></HStack>
      ) : isUnauthorized ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>Not authorized</Heading>
            <Text color="gray.500">You do not have permission to view leads.</Text>
          </CardBody>
        </Card>
      ) : sortedLeads.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No leads in this status</Heading>
            <Text color="gray.500">
              Leads will appear here after they are received or moved into {statusLabel(activeStatus).toLowerCase()}.
            </Text>
          </CardBody>
        </Card>
      ) : isMobile ? (
        renderMobile()
      ) : (
        renderDesktop()
      )}

      <Drawer isOpen={detailsDrawer.isOpen} placement="right" onClose={detailsDrawer.onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Lead details</DrawerHeader>
          <DrawerBody>
            {selectedLead ? (
              <LeadDetailsContent
                lead={selectedLead}
                user={user}
                isUpdating={Boolean(updatingLeadIds[selectedLead.id])}
                onStatusChange={(status) => persistStatusUpdate(selectedLead.id, status)}
              />
            ) : null}
          </DrawerBody>

          <DrawerFooter>
            <HStack>
              {bookingUrl(selectedLead) && (
                <Button
                  as="a"
                  href={bookingUrl(selectedLead)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                >
                  Open booking link
                </Button>
              )}
              <Button onClick={detailsDrawer.onClose}>Close</Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default LeadsList;
